// "Mission Update" — the mandatory monthly reflection. Each team member of a
// customer with a goal must, at least once a month, report the current value of
// every criterion (metric) and write a short reflection. Early in the month it
// can be snoozed; from CHECKIN_HARD_DAY on it becomes non-dismissable.
import { useEffect, useState } from 'react';
import { useData } from './DataContext.jsx';
import { MentionTextarea } from './mention-input.jsx';
import { IconX, IconFlag } from './icons.jsx';
import {
  currentMonthKey, addMonthsKey, metricMonthTarget, metricActual, metricMonthValue,
  fmtGoalValue, fmtCadence, fmtMonthLong, STATUS_META,
} from './lib/goals.js';

const CONFIDENCE_OPTS = ['on_track', 'at_risk', 'off_track'];

function MonthlyCheckinModal({ customer, goal, metrics, progressByMetric, dismissable, queueInfo, onSubmitted, onSnooze }) {
  const { submitCheckin, profiles, syncMentions } = useData();
  const mk = currentMonthKey();
  const prevMk = addMonthsKey(mk, -1);
  const list = metrics || [];

  // Per-metric input value (string), seeded sensibly per type.
  const [values, setValues] = useState(() => {
    const map = {};
    for (const m of list) {
      const rows = progressByMetric[m.id] || [];
      const thisMonth = metricMonthValue(rows, mk);
      if (thisMonth != null) map[m.id] = String(thisMonth);
      else if (m.type === 'recurring') map[m.id] = '';
      else map[m.id] = String(metricActual(m, rows, mk)); // carry forward latest total
    }
    return map;
  });
  const [reflection, setReflection] = useState('');
  const [confidence, setConfidence] = useState('on_track');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && dismissable) onSnooze?.(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [dismissable, onSnooze]);

  const allValuesOk = list.every(m => {
    const v = values[m.id];
    return v !== '' && v != null && !Number.isNaN(parseFloat(v));
  });
  const canSubmit = allValuesOk && reflection.trim().length >= 10 && !saving;

  async function submit() {
    if (!canSubmit) return;
    setSaving(true); setErr(null);
    try {
      const payloadValues = list.map(m => ({ metric_id: m.id, value: parseFloat(values[m.id]) }));
      await submitCheckin(goal.id, mk, { values: payloadValues, reflection, confidence });
      await syncMentions({
        text: reflection,
        sourceType: 'checkin',
        sourceId: goal.id,
      });
      onSubmitted?.();
    } catch (e) {
      setErr(e.message || 'Fehler beim Speichern');
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && dismissable) onSnooze?.(); }}>
      <div className="modal" style={{ width: 600 }}>
        <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid var(--stroke)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconFlag size={12}/> Monats-Check-in · {fmtMonthLong(mk)}
              {queueInfo && queueInfo.total > 1 && (
                <span className="muted" style={{ fontWeight: 400 }}> · {queueInfo.index + 1}/{queueInfo.total}</span>
              )}
            </div>
            <h2 className="serif" style={{ fontSize: 21, fontWeight: 500, margin: '6px 0 0', letterSpacing: '-0.01em', lineHeight: 1.25 }}>
              {goal.title}
            </h2>
            <div className="tiny muted" style={{ marginTop: 4 }}>{customer.name}</div>
          </div>
          {dismissable && (
            <button className="icon-btn" onClick={() => onSnooze?.()} title="Später heute"><IconX size={14}/></button>
          )}
        </div>

        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Aktueller Stand je Kriterium *</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {list.map(m => {
              const rows = progressByMetric[m.id] || [];
              const target = metricMonthTarget(m, goal, mk);
              const prev = metricMonthValue(rows, prevMk);
              const isRecurring = m.type === 'recurring';
              const v = values[m.id];
              const num = parseFloat(v);
              const delta = (!isRecurring && !Number.isNaN(num) && prev != null) ? num - prev : null;
              return (
                <div key={m.id} style={{ padding: 12, background: 'var(--bg-sunken)', border: '1px solid var(--stroke)', borderRadius: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{m.label}</div>
                      <div className="tiny muted">
                        {isRecurring
                          ? <>Ziel: {fmtCadence(m)} · {fmtGoalValue(target, m.unit)} diesen Monat</>
                          : <>Soll diesen Monat: {fmtGoalValue(target, m.unit)}{prev != null ? ` · Vormonat ${fmtGoalValue(prev, m.unit)}` : ''}</>}
                      </div>
                    </div>
                    {delta != null && (
                      <span className="tiny" style={{ color: delta >= 0 ? 'var(--ok)' : 'var(--crit)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {delta >= 0 ? '+' : ''}{fmtGoalValue(delta, '')}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input className="input" type="number" value={v ?? ''} style={{ background: 'var(--bg-card)', maxWidth: 200 }}
                           onChange={e => setValues(s => ({ ...s, [m.id]: e.target.value }))}
                           placeholder={isRecurring ? 'Anzahl diesen Monat' : 'Aktueller Gesamtstand'}/>
                    {m.unit && <span className="tiny muted">{m.unit}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="field" style={{ marginBottom: 18 }}>
            <label>Wie steht's gerade? *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {CONFIDENCE_OPTS.map(k => {
                const meta = STATUS_META[k];
                const active = confidence === k;
                return (
                  <button key={k} onClick={() => setConfidence(k)} className="btn"
                          style={{
                            flex: 1, justifyContent: 'center',
                            borderColor: active ? meta.color : 'var(--stroke)',
                            background: active ? meta.soft : 'transparent',
                            color: active ? meta.color : 'var(--ink-2)',
                            fontWeight: active ? 600 : 500,
                          }}>
                    {meta.emoji} {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="field">
            <label>
              Kurze Reflexion *
              <span className="tiny muted" style={{ marginLeft: 8, fontWeight: 500 }}>
                {reflection.trim().length < 10 ? `noch ${10 - reflection.trim().length} Zeichen` : '✓'}
              </span>
            </label>
            <MentionTextarea value={reflection} onChange={setReflection} profiles={profiles}
                      placeholder="Was lief diesen Monat aufs Ziel ein? Was hat funktioniert, wo hakt es, was nimmst du dir vor?"
                      style={{ minHeight: 100 }}/>
          </div>

          {err && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 12 }}>{err}</div>}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--stroke)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          {dismissable ? (
            <button className="btn btn-ghost" onClick={() => onSnooze?.()} disabled={saving}>Später heute</button>
          ) : (
            <span className="tiny" style={{ color: 'var(--warn)' }}>Fällig — bitte jetzt eintragen.</span>
          )}
          <button className="btn btn-primary" onClick={submit} disabled={!canSubmit}
                  style={{ opacity: canSubmit ? 1 : 0.45 }}>
            {saving ? 'Speichere…' : 'Check-in abschließen'}
          </button>
        </div>
      </div>
    </div>
  );
}

export { MonthlyCheckinModal };
